import React, {useState, useEffect} from 'react'
import {Modal, Button, Form, Spinner} from 'react-bootstrap'
import Loader from 'src/utils/Loader'
import {
  getPlatformOwnerFieldsAsync,
  updatePlatformAdditionalInfoStatusAsync,
} from 'src/redux/actions/platformActions'
import {useAppDispatch} from 'src/redux/storeHooks'
import {saveFieldMappingAsync} from 'src/redux/actions/productActions'
import useAppToast from 'src/hooks/useAppToast'

const PlatformAdditionalMappingModal = ({
  selectedPlatformId,
  onComplete,
}: {
  selectedPlatformId?: string
  onComplete?: () => void
}) => {
  const [selectedOptions, setSelectedOptions] = useState({})
  const [loading, setLoading] = useState(true)
  const [show, setShow] = useState(true)
  const [inputFields, setInputFields] = useState<any>([])
  const dispatch = useAppDispatch()
  const {successToast, errorToast} = useAppToast()

  useEffect(() => {
    if (!selectedPlatformId) return

    const platformId = selectedPlatformId

    const fetchFields = async () => {
      setLoading(true)
      try {
        let baseResponse = await dispatch(
          getPlatformOwnerFieldsAsync({platform_id: platformId, field_id: ''})
        ).unwrap()

        // Find mappings with hasmore = true
        const mappingsWithMore = baseResponse.data.filter(
          (mapping: any) => mapping?.hasmore === true && mapping?.child_field_set?.length > 0
        )

        const childFieldResults: any[] = []

        for (const mapping of mappingsWithMore) {
          if (Array.isArray(mapping.child_field_set)) {
            for (const fieldId of mapping.child_field_set) {
              try {
                // Avoid refetching already-present fields; only fetch distinct child fields
                const isSameField = baseResponse.data.find((item) => item.id === fieldId)?.id
                if (fieldId !== isSameField) {
                  const res = await dispatch(
                    getPlatformOwnerFieldsAsync({
                      platform_id: platformId,
                      field_id: fieldId.toString(),
                    })
                  ).unwrap()

                  // Assuming res.data is the array you want to collect
                  childFieldResults.push(...res.data)
                }
              } catch (err) {
                console.error(`Error fetching child field ${fieldId}`, err)
              }
            }
          }
        }
        if (childFieldResults.length) baseResponse.data.push(...childFieldResults)
        setInputFields(baseResponse)
      } catch (error) {
        console.error('Error fetching fields:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchFields()
  }, [selectedPlatformId, dispatch])


  const handleSelectChange = (e, id) => {
    const value = e.target.value
    setSelectedOptions((prev) => ({...prev, [id]: value}))
  }

  // Check if all required selects are filled
  const isFormValid = inputFields?.data?.every((input) => selectedOptions[input.id])

  const handleSubmit = async () => {
    if (!selectedPlatformId) return

    try {
      const selectedValues = Object.entries(selectedOptions)

      const allSaveMappingPromises: Promise<any>[] = []

      for (const [key, value] of selectedValues) {
        const inputField = inputFields?.data?.find((input) => input.id === Number(key))
        if (!inputField) continue

        const selectedOption = inputField.platform_field_data_set.find(
          (option) => option.id === Number(value)
        )

        if (!selectedOption) continue

        const payload = {
          source_field_id: null,
          destination_field_id: Number(key),
          fieldmappingdata_set: [
            {
              destination_platform_data_id: Number(selectedOption.id),
            },
          ],
        }

        const savePromise = dispatch(saveFieldMappingAsync(payload))
        allSaveMappingPromises.push(savePromise)
      }

      const results = await Promise.all(allSaveMappingPromises)

      const allSuccessful = results.length > 0 &&
      results.every((response) => response?.meta?.requestStatus === 'fulfilled');
    
      if (allSuccessful) {
        dispatch(updatePlatformAdditionalInfoStatusAsync({platform_id: selectedPlatformId}))
        setShow(false)
        onComplete?.()
        successToast('Channel settings saved successfully')
      } else {
        errorToast('Failed to save channel settings')
      }
    } catch (error) {
      errorToast(`Error submitting form: ${error}`)}
  }

  return (
    <Modal show={show} centered>
      <Modal.Header className='bg-secondary text-light fs-5' style={{fontWeight: '500'}}>
        Channel is connected. One step more to finish the connection.
      </Modal.Header>
      <Modal.Body
        style={{
          minHeight: '150px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading ? (
          <div
            className='text-secondary position-absolute'
            style={{top: '40%', left: '48%'}}
          >
            <Loader variant="section" />
          </div>
        ) : (
          <>
            {inputFields?.data?.length > 0 &&
              inputFields.data.map((input, index) => {
                const isFirst = index === 0
                const prevInput = inputFields.data[index - 1]
                const selectedPrevId = prevInput ? selectedOptions[prevInput.id] : null

                // Enable logic
                const isEnabled = isFirst || (prevInput && selectedPrevId)

                // Filter options based on selected previous option
                const filteredOptions = isFirst
                  ? input.platform_field_data_set
                  : input.platform_field_data_set.filter(
                      (option) => option.parent_data === Number(selectedPrevId)
                    )

                    return (
                  <div key={input.id} className='row mt-3'>
                    <div className='col-md-3 col-6 align-self-center'>
                      <label className='form-label m-0'>{input.display_name}</label>
                    </div>
                    <div className='col-sm-6 col-12'>
                      <select
                        value={selectedOptions[input.id] || ''}
                        onChange={(e) => handleSelectChange(e, input.id)}
                        className='form-control'
                        disabled={!isEnabled}
                      >
                        <option value=''>Select Option</option>
                        {filteredOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.api_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )
              })}

            <div className='w-100 d-flex justify-content-end mt-3'>
              <Button
                variant='success'
                className='py-2 px-3 fw-bold'
                disabled={!isFormValid}
                onClick={handleSubmit}
              >
                Finish
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default PlatformAdditionalMappingModal
